import React, { useMemo } from 'react';
import GeoBrandLogo from './GeoBrandLogo';
import GeoBrandLogoChartMarker from './GeoBrandLogoChartMarker';
import GeoQuadrantPill from './GeoQuadrantPill';
import GeoMetricTitle from './GeoMetricTitle';
import GeoTableHeaderLabel from './GeoTableHeaderLabel';
import { formatCompanyName, geoSvgAxisFontSize } from '../utils/geoPresentation';
import { getGeoBrandColorForBrand } from '../utils/geoBrandColors';

const CHART_WIDTH = 380;
const CHART_HEIGHT = 188;
const PAD = { top: 18, right: 16, bottom: 30, left: 48 };
const CHART_LOGO_SIZE = 10;
const AXIS_TICKS = [0, 25, 50, 75, 100];
/** viewBox units — CSS px on SVG text does not scale with preserveAspectRatio */
const AXIS_TICK_FONT = geoSvgAxisFontSize(CHART_HEIGHT, 10);
const AXIS_LABEL_FONT = geoSvgAxisFontSize(CHART_HEIGHT, 11);
/** SVG user units — scale with viewBox (CSS px on SVG text does not). */
const FONT = {
  quadrant: 5,
};

function scaleX(value, plotW) {
  return PAD.left + (Math.min(Math.max(value, 0), 100) / 100) * plotW;
}

function scaleY(value, plotH) {
  return PAD.top + plotH - (Math.min(Math.max(value, 0), 100) / 100) * plotH;
}

export default function GeoBrandVisibilityIndex({
  data = { thresholds: { brandCoverage: 50, likelihoodToBuy: 50 }, brands: [] },
  brandColorMap,
  loading = false,
}) {
  const thresholds = data.thresholds || { brandCoverage: 50, likelihoodToBuy: 50 };
  const brands = data.brands || [];
  const plotW = CHART_WIDTH - PAD.left - PAD.right;
  const plotH = CHART_HEIGHT - PAD.top - PAD.bottom;

  const { points, midX, midY } = useMemo(() => ({
    midX: PAD.left + plotW / 2,
    midY: PAD.top + plotH / 2,
    points: brands.map((brand) => ({
      ...brand,
      cx: scaleX(brand.brandCoverage, plotW),
      cy: scaleY(brand.likelihoodToBuy, plotH),
      color: getGeoBrandColorForBrand(brand.brand, brandColorMap),
    })),
  }), [brands, plotW, plotH, brandColorMap]);

  const sortedBrands = useMemo(
    () => [...brands].sort((a, b) => b.brandCoverage - a.brandCoverage || b.likelihoodToBuy - a.likelihoodToBuy),
    [brands],
  );

  return (
    <div className="report-card volume-chart-panel geo-insights-visibility-card">
      <div className="chart-panel__header">
        <GeoMetricTitle metricKey="ai_visibility_index">AI Visibility Index</GeoMetricTitle>
      </div>
      <div className="geo-insights-visibility-body">
        <div className="geo-insights-visibility-chart-wrap">
          {loading ? (
            <div className="geo-insights-empty geo-insights-empty--compact">Loading chart…</div>
          ) : brands.length === 0 ? (
            <div className="geo-insights-empty geo-insights-empty--compact">
              <p>No visibility data in this range</p>
            </div>
          ) : (
            <svg
              className="geo-insights-visibility-chart"
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              style={{ aspectRatio: `${CHART_WIDTH} / ${CHART_HEIGHT}` }}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label="AI visibility index quadrant chart"
            >
              <rect
                x={PAD.left}
                y={PAD.top}
                width={plotW}
                height={plotH}
                className="geo-insights-visibility-chart__plot-bg"
              />
              <line
                x1={PAD.left}
                y1={PAD.top}
                x2={PAD.left}
                y2={PAD.top + plotH}
                className="geo-insights-visibility-chart__axis-line"
              />
              <line
                x1={PAD.left}
                y1={PAD.top + plotH}
                x2={PAD.left + plotW}
                y2={PAD.top + plotH}
                className="geo-insights-visibility-chart__axis-line"
              />
              <line
                x1={PAD.left}
                y1={midY}
                x2={PAD.left + plotW}
                y2={midY}
                className="geo-insights-visibility-chart__midline"
              />
              <line
                x1={midX}
                y1={PAD.top}
                x2={midX}
                y2={PAD.top + plotH}
                className="geo-insights-visibility-chart__midline"
              />
              <text x={PAD.left + 6} y={PAD.top + 12} fontSize={FONT.quadrant} className="geo-insights-visibility-chart__quadrant-label geo-insights-visibility-chart__quadrant-label--niche">
                Niche
              </text>
              <text x={PAD.left + plotW - 6} y={PAD.top + 12} fontSize={FONT.quadrant} textAnchor="end" className="geo-insights-visibility-chart__quadrant-label geo-insights-visibility-chart__quadrant-label--leaders">
                Leaders
              </text>
              <text x={PAD.left + 6} y={PAD.top + plotH - 6} fontSize={FONT.quadrant} className="geo-insights-visibility-chart__quadrant-label geo-insights-visibility-chart__quadrant-label--low-performance">
                Low Performance
              </text>
              <text x={PAD.left + plotW - 6} y={PAD.top + plotH - 6} fontSize={FONT.quadrant} textAnchor="end" className="geo-insights-visibility-chart__quadrant-label geo-insights-visibility-chart__quadrant-label--low-conversion">
                Low Conversion
              </text>
              {AXIS_TICKS.map((tick) => (
                <text
                  key={`y-${tick}`}
                  x={PAD.left - 10}
                  y={scaleY(tick, plotH) + 4}
                  fontSize={AXIS_TICK_FONT}
                  textAnchor="end"
                  className="geo-insights-visibility-chart__tick"
                >
                  {tick}
                </text>
              ))}
              {AXIS_TICKS.map((tick) => (
                <text
                  key={`x-${tick}`}
                  x={scaleX(tick, plotW)}
                  y={PAD.top + plotH + 14}
                  fontSize={AXIS_TICK_FONT}
                  textAnchor="middle"
                  className="geo-insights-visibility-chart__tick"
                >
                  {tick}
                </text>
              ))}
              <text
                x={PAD.left + plotW / 2}
                y={PAD.top + plotH + 24}
                fontSize={AXIS_LABEL_FONT}
                textAnchor="middle"
                className="geo-insights-visibility-chart__axis-label"
              >
                Mention share (%)
              </text>
              <text
                x={14}
                y={PAD.top + plotH / 2}
                fontSize={AXIS_LABEL_FONT}
                textAnchor="middle"
                transform={`rotate(-90 14 ${PAD.top + plotH / 2})`}
                className="geo-insights-visibility-chart__axis-label"
              >
                Purchase intent (%)
              </text>
              {points.map((point) => (
                <GeoBrandLogoChartMarker
                  key={point.brand}
                  x={point.cx}
                  y={point.cy}
                  size={CHART_LOGO_SIZE}
                  color={point.color}
                  logoUrl={point.logoUrl}
                  name={formatCompanyName(point.brand)}
                  isClient={point.isClient}
                />
              ))}
            </svg>
          )}
        </div>

        <div className="geo-insights-visibility-table-wrap">
          <table className="sec-filing-insights-data-table geo-insights-data-table geo-insights-visibility-table">
            <thead>
              <tr>
                <th className="col-brand">Company</th>
                <th className="col-quadrant">Quadrant</th>
                <th className="col-metric">Mention share</th>
                <th className="col-metric">
                  <GeoTableHeaderLabel metricKey="purchase_intent">Purchase intent</GeoTableHeaderLabel>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4}>Loading…</td>
                </tr>
              ) : sortedBrands.length === 0 ? (
                <tr>
                  <td colSpan={4}>No company data in this range</td>
                </tr>
              ) : (
                sortedBrands.map((row, index) => {
                  const displayName = formatCompanyName(row.brand);
                  return (
                    <tr
                      key={row.brand}
                      className={row.isClient ? 'geo-insights-row--client' : ''}
                      style={{ '--table-row-delay': index }}
                    >
                      <td className="col-brand">
                        <span className="geo-brand-name-cell">
                          <GeoBrandLogo
                            name={displayName}
                            color={getGeoBrandColorForBrand(row.brand, brandColorMap)}
                            logoUrl={row.logoUrl}
                            isClient={row.isClient}
                          />
                          <span className="geo-brand-name-cell__label" title={displayName}>
                            {displayName}
                          </span>
                        </span>
                      </td>
                      <td className="col-quadrant">
                        <GeoQuadrantPill quadrantKey={row.quadrantKey} />
                      </td>
                      <td className="col-metric">{row.brandCoverage.toFixed(1)}%</td>
                      <td className="col-metric">{row.likelihoodToBuy.toFixed(0)}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
